import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  getSelectStateClass,
  Invoice,
  mapDeliveryStatusColor,
  mapDeliveryStatusLabel,
  mapStatusTracking,
  PriceType,
  Status,
  StatusTrackingOptions,
  toDeliveryStatusList,
  TypeBill,
} from '@app/shared/interfaces/invoice.interface';
import { MESSAGES } from '@app/shared/utils/messages';
import { Pageable } from '@app/shared/interfaces/pagination.interface';
import { ButtonIcon, ButtonSize, ButtonStyle } from '@app/shared/components/atoms/buttons/button.type';
import { Style, Size as IconSize, TooltipPosition } from '@app/shared/components/atoms/icon/icon.component';
import { InvoicesService } from '@app/components/invoice/invoices.service';
import { debounceTime, lastValueFrom, Subject } from 'rxjs';
import { AlertService } from '@app/shared/components/organisms/alert/alert.service';
import {
  InvoicesStatusEnum,
  mapInvoiceStatusToRequest,
  mapTypeBill,
  mapPriceType,
  mapPriceTypeToColor,
  mapTypeFailedEmailsToLibelle,
  ListInvoiceSchema,
  mapPriceTypeForExport,
  normalizeQuantityToMWh,
} from '@app/components/invoice/invoices.model';
import { ConfirmDialogData } from '@app/shared/components/organisms/dialog/dialog.data';
import { DialogService } from '@app/shared/components/organisms/dialog/dialog.service';
import { Color } from '@app/shared/models/color.model';
import { SelectionModel } from '@angular/cdk/collections';
import { downloadCsv, formatFormulaLabel, formatEmails, toCsv } from '@app/shared/utils/utils';
import { SettingsService } from '../settings/settings.service';
import { ToggleSwitchSize } from '@app/shared/models/toggle-switch.model';
import { FilterItem } from '@app/shared/components/organisms/table/column-header/column-header.component';
import { TableSort } from '@app/shared/components/organisms/table/server-table/server-table.component';
import { DateTime } from 'luxon';
import { WebsocketEventType, WebsocketService } from '@app/shared/websocket/websocket.service';

const DEFAULT_PAGE_SIZE: number = 10;

const INVOICE_FILTERABLE_FIELDS = ['typeBill', 'formulaLabel', 'tracking'] as const;

type InvoiceArchivedCsvRow = {
  facture: string;
  producteur: string;
  site: string;
  prm: string;
  filiere: string;
  periodeFacturation: string;
  dateEmission: string;
  montantHt: string;
  volumeMWh: string;
  formule: string;
  typeM0: string;
  typeBill: string;
  suivi: string;
  contact: string;
  commentaire: string;
};

type InvoiceTodoCsvRow = {
  etat: string;
  dateGeneration: string;
  facture: string;
  producteur: string;
  site: string;
  prm: string[];
  filiere: string;
  periodeFacturation: string;
  montantHt: string;
  volumeMWh: string;
  formule: string;
  typeBill: string;
  typeM0: string;
  contact: string;
  commentaire: string;
};

const invoiceArchivedHeaders: { key: keyof InvoiceArchivedCsvRow; label: string }[] = [
  { key: 'facture', label: 'Facture' },
  { key: 'producteur', label: 'Producteur' },
  { key: 'site', label: 'Site' },
  { key: 'prm', label: 'PRM' },
  { key: 'filiere', label: 'Filière' },
  { key: 'periodeFacturation', label: 'Période de facturation' },
  { key: 'dateEmission', label: "Date d'émission" },
  { key: 'montantHt', label: 'Montant HT' },
  { key: 'volumeMWh', label: 'Volume MWh' },
  { key: 'formule', label: 'Formule' },
  { key: 'typeBill', label: 'Type document' },
  { key: 'typeM0', label: 'Type M0' },
  { key: 'suivi', label: 'Suivi' },
  { key: 'contact', label: 'Contact' },
  { key: 'commentaire', label: 'Commentaire' },
];

const invoiceTodoHeaders: { key: keyof InvoiceTodoCsvRow; label: string }[] = [
  { key: 'etat', label: 'État' },
  { key: 'dateGeneration', label: 'Date de génération' },
  { key: 'facture', label: 'Facture' },
  { key: 'producteur', label: 'Producteur' },
  { key: 'site', label: 'Site' },
  { key: 'prm', label: 'PRM' },
  { key: 'filiere', label: 'Filière' },
  { key: 'periodeFacturation', label: 'Période de facturation' },
  { key: 'montantHt', label: 'Montant HT' },
  { key: 'volumeMWh', label: 'Volume MWh' },
  { key: 'formule', label: 'Formule' },
  { key: 'typeBill', label: 'Type document' },
  { key: 'typeM0', label: 'Type M0' },
  { key: 'contact', label: 'Contact' },
  { key: 'commentaire', label: 'Commentaire' },
];

export interface InvoiceKeySource {
  timestamp: string;
  prm: string;
}

@Component({
  selector: 'app-invoices',
  templateUrl: './invoices.component.html',
  styleUrls: ['./invoices.component.scss'],
  standalone: false,
})
export class InvoicesComponent implements OnInit, OnDestroy {
  ToggleSwitchSize = ToggleSwitchSize;

  pageInvoices: Invoice[] = [];

  pageable: Pageable<Invoice> = {
    content: [],
    total: 0,
    limit: DEFAULT_PAGE_SIZE,
    offset: 0,
  };

  risSelection = new SelectionModel<string>(true, []);

  private selectedInvoices = new Map<string, Invoice>();

  isLoading: boolean = true;

  totalInvoiceNumber: number = 0;

  threshold: number = 0;

  quota: number = 0;

  rangeStartDate: string = '';

  rangeEndDate: string = '';

  downloadLoaders: boolean[] = [];

  loaderSending: boolean = false;

  sendingLoaders: boolean[] = [];

  currentPage: number = 0;

  currentPageSize: number = DEFAULT_PAGE_SIZE;

  invoicesNumberForSend: number = 0;

  invoicesValidateNumber: number = 0;

  invoicesStatus: InvoicesStatusEnum = InvoicesStatusEnum.EN_COURS;

  protected readonly InvoicesStatusEnum = InvoicesStatusEnum;

  noDataMessage: string = MESSAGES.NO_DATA_ERROR;

  protected readonly TooltipPosition = TooltipPosition;

  protected readonly IconSize = IconSize;

  protected readonly ButtonIcon = ButtonIcon;

  protected readonly ButtonSize = ButtonSize;

  protected readonly Style = Style;

  protected readonly Object = Object;

  protected readonly Status = Status;

  protected readonly Color = Color;

  protected readonly ButtonStyle = ButtonStyle;

  protected readonly formatFormulaLabel = formatFormulaLabel;

  protected readonly formatEmails = formatEmails;

  protected readonly statusTrackingOptions = StatusTrackingOptions;

  protected readonly mapStatusTracking = mapStatusTracking;

  protected readonly toDeliveryStatusList = toDeliveryStatusList;

  protected readonly mapDeliveryStatusLabel = mapDeliveryStatusLabel;

  protected readonly mapDeliveryStatusColor = mapDeliveryStatusColor;

  protected readonly getSelectStateClass = getSelectStateClass;

  protected readonly TypeBill = TypeBill;

  protected readonly PriceType = PriceType;

  protected readonly mapTypeBill = mapTypeBill;

  protected readonly mapPriceType = mapPriceType;

  protected readonly mapPriceTypeToColor = mapPriceTypeToColor;

  protected readonly mapTypeFailedEmailsToLibelle = mapTypeFailedEmailsToLibelle;

  protected filterItems: { [key: string]: FilterItem[] } = {};

  hoveredTooltipId: number | null = null;

  numberInvoiceError: number | null = null;

  public searchTerm: string = '';

  private appliedSearch: string = '';

  selectedFilter = '';

  sortField: string = 'dateGenerated';

  sortDirection: 'asc' | 'desc' = 'desc';

  columnFilters: Record<string, string[]> = {};

  private latestRequestId = 0;

  private refreshRequests$ = new Subject<void>();

  filterOptions() {
    const defaultOptions = [
      { value: 'dateGenerated', label: 'Date de génération' },
      { value: 'DateDebutCalcul', label: 'Période de facturation' },
    ];

    if (this.invoicesStatus === InvoicesStatusEnum.ERROR) {
      return defaultOptions;
    }

    if (this.invoicesStatus === InvoicesStatusEnum.ARCHIVE) {
      return [
        { value: 'sentDate', label: "Date d'émission" },
        { value: 'DateDebutCalcul', label: 'Période de facturation' },
      ];
    }
    return defaultOptions;
  }

  protected readonly downloadStatus: Status[] = [Status.GENERATED, Status.SENT, Status.VALIDATED, Status.REFUSED];

  constructor(
    private dialogService: DialogService,
    private invoicesService: InvoicesService,
    private alertService: AlertService,
    private settingService: SettingsService,
    private websocketService: WebsocketService,
  ) {}

  ngOnDestroy(): void {
    this.pageInvoices = [];
    this.refreshRequests$.complete();
  }

  ngOnInit() {
    this.setDefaultSort();
    this.fetchInvoices().then();

    this.refreshRequests$.pipe(debounceTime(500)).subscribe(() => this.fetchInvoices(false));

    this.listenInvoicesUpdated();
  }

  listenInvoicesUpdated(): void {
    this.websocketService
      .onInvoicesUpdated()
      .pipe()
      .subscribe(({ newItem, oldItem, type }) => {
        this.updateSingleInvoiceRow(newItem as Invoice, oldItem as Invoice, type);
      });
  }

  private setDefaultSort() {
    this.sortField = this.invoicesStatus === InvoicesStatusEnum.ARCHIVE ? 'sentDate' : 'dateGenerated';
    this.sortDirection = 'desc';
  }

  /**
   * Construit les paramètres d'appel à partir de l'état courant (onglet, recherche,
   * filtre date, filtres colonnes, tri). Le filtre date n'est envoyé que si un type
   * est explicitement sélectionné ET qu'au moins une borne est saisie.
   * @param paged si false, ne pagine pas : utilisé pour l'export et l'envoi groupé
   */
  private buildRequestParams(paged = true): { [key: string]: string } {
    const params: { [key: string]: string } = {
      status: mapInvoiceStatusToRequest(this.invoicesStatus).join(','),
    };

    if (this.appliedSearch) {
      params.search = this.appliedSearch;
    }

    if (this.isDateFilterActive()) {
      params.selectedFilter = this.selectedFilter;
      if (this.rangeStartDate) params.startDate = this.rangeStartDate;
      if (this.rangeEndDate) params.endDate = this.rangeEndDate;
    }

    const activeFilters = Object.fromEntries(Object.entries(this.columnFilters).filter(([, values]) => values.length));
    if (Object.keys(activeFilters).length) {
      params.filters = JSON.stringify(activeFilters);
    }

    params.sortField = this.sortField;
    params.sortDirection = this.sortDirection;

    if (paged) {
      params.limit = String(this.currentPageSize);
      params.offset = String(this.currentPage * this.currentPageSize);
    }

    return params;
  }

  private async fetchInvoices(showLoader = true): Promise<void> {
    const requestId = ++this.latestRequestId;

    if (showLoader) this.isLoading = true;

    try {
      const response = await lastValueFrom(this.invoicesService.getInvoices(this.buildRequestParams()));

      // Une requete plus récente existe
      if (requestId !== this.latestRequestId) return;

      const total = response.list.Count;
      if (this.currentPage > 0 && total > 0 && this.currentPage * this.currentPageSize >= total) {
        // La page courante n'existe plus (suppression/envoi) : on retombe sur la dernière page
        this.currentPage = Math.ceil(total / this.currentPageSize) - 1;
        return this.fetchInvoices(showLoader);
      }

      const isArchive = this.invoicesStatus === InvoicesStatusEnum.ARCHIVE;
      if (isArchive) {
        const result = ListInvoiceSchema.safeParse(response.list);
        if (!result.success) {
          console.error('Réponse API invalide :', result.error);
        }
      }

      this.numberInvoiceError = response.list_error.Count ?? response.list_error.Items.length;

      // Les factures pré-MEP sont reçues en kWh : on affiche toujours en MWh
      const items = response.list.Items.map((item: Invoice) => normalizeQuantityToMWh(item));

      this.pageInvoices = items;
      this.pageable = {
        content: items,
        total,
        limit: this.currentPageSize,
        offset: this.currentPage * this.currentPageSize,
      };

      this.totalInvoiceNumber = response.counters?.total ?? total;
      this.invoicesValidateNumber = response.counters?.validated ?? 0;

      this.downloadLoaders = [];
      this.sendingLoaders = [];

      this.updateFilterItems(response.filterValues ?? {});
      this.onInvoiceRequest();
    } catch (error) {
      console.error(`Erreur lors de la récupération des factures au statut : ${this.invoicesStatus}`, error);
    } finally {
      if (requestId === this.latestRequestId) this.isLoading = false;
    }
  }

  /**
   * Reconstruit les listes des filtres de colonnes à partir des valeurs renvoyées par
   * le serveur, en conservant les cases cochées par l'utilisateur.
   */
  private updateFilterItems(filterValues: { [key: string]: string[] }) {
    const updated: { [key: string]: FilterItem[] } = {};

    INVOICE_FILTERABLE_FIELDS.forEach((field) => {
      const activeValues = this.columnFilters[field] ?? [];
      updated[field] = (filterValues[field] ?? []).map((name) => ({
        name,
        checked: activeValues.includes(name),
      }));
    });

    this.filterItems = updated;
  }

  private scheduleSilentRefresh() {
    this.refreshRequests$.next();
  }

  async triggerSearch() {
    this.appliedSearch = this.searchTerm.trim();
    this.currentPage = 0;
    await this.fetchInvoices();
  }

  getInvoiceKey({ timestamp, prm }: InvoiceKeySource): string {
    return `${timestamp}::${prm}`;
  }

  onPageableChange(pageable: Pageable<Invoice>) {
    this.currentPage = Math.floor(pageable.offset / pageable.limit);
    this.currentPageSize = pageable.limit;
    this.fetchInvoices().then();
  }

  async onSortChange(sort: TableSort) {
    if (this.sortField === sort.field && this.sortDirection === sort.direction) return;

    this.sortField = sort.field;
    this.sortDirection = sort.direction;
    await this.fetchInvoices();
  }

  async onColumnFiltersChange(filters: Record<string, string[]>) {
    if (JSON.stringify(filters) === JSON.stringify(this.columnFilters)) return;

    this.columnFilters = filters;
    this.currentPage = 0;
    await this.fetchInvoices();
  }

  /**
   * appel que si le filtre date était deja rempli
   */
  async onFilterChanged(newFilter: string) {
    const wasDateFilterActive = this.isDateFilterActive();
    this.selectedFilter = newFilter;

    if (!newFilter) {
      this.rangeStartDate = '';
      this.rangeEndDate = '';
    }

    if (!wasDateFilterActive && !this.isDateFilterActive()) return;

    this.currentPage = 0;
    await this.fetchInvoices();
  }

  private isDateFilterActive(): boolean {
    return Boolean(this.selectedFilter && (this.rangeStartDate || this.rangeEndDate));
  }

  async onStartDateChanged(newStartDate: string) {
    this.rangeStartDate = newStartDate;

    if (!this.selectedFilter) return;

    this.currentPage = 0;
    await this.fetchInvoices();
  }

  async onEndDateChanged(newEndDate: string) {
    this.rangeEndDate = newEndDate;

    if (!this.selectedFilter) return;

    this.currentPage = 0;
    await this.fetchInvoices();
  }

  masterPageToggle(select: boolean) {
    const invoicesSelectable = this.pageInvoices.filter((data) => this.isSelectable(data));

    invoicesSelectable.forEach((invoice) => {
      const key = this.getInvoiceKey(invoice);

      if (select) {
        this.risSelection.select(key);
        this.selectedInvoices.set(key, invoice);
      } else {
        this.risSelection.deselect(key);
        this.selectedInvoices.delete(key);
      }
    });
  }

  toggleSelection(invoice: Invoice) {
    const key = this.getInvoiceKey(invoice);

    this.risSelection.toggle(key);

    if (this.risSelection.isSelected(key)) {
      this.selectedInvoices.set(key, invoice);
    } else {
      this.selectedInvoices.delete(key);
    }
  }

  onInvoiceRequest() {
    this.settingService.getSettings().subscribe({
      next: (response) => {
        this.quota = response.quota ?? 0;
        this.threshold = response.threshold ?? 0;
        this.invoicesNumberForSend = Math.ceil((this.quota * this.totalInvoiceNumber) / 100) || 1;
      },
      error: () => {
        this.alertService.error(MESSAGES.DEFAULT_ERROR);
      },
    });
  }

  checkboxLabel(selectionId?: string): string {
    if (!selectionId) {
      return `${this.isAllPageSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.risSelection.isSelected(selectionId) ? 'deselect' : 'select'} row ${selectionId + 1}`;
  }

  isAllPageSelected() {
    const selectable = this.pageInvoices.filter((data) => this.isSelectable(data));
    return (
      selectable.length > 0 && selectable.every((invoice) => this.risSelection.isSelected(this.getInvoiceKey(invoice)))
    );
  }

  isCurrentPageSelected() {
    const selectable = this.pageInvoices.filter((data) => this.isSelectable(data));
    const selectedCount = selectable.filter((invoice) =>
      this.risSelection.isSelected(this.getInvoiceKey(invoice)),
    ).length;

    return selectedCount > 0 && selectedCount < selectable.length;
  }

  async onSelectStatusChange(row: Invoice, selectedValue: string): Promise<void> {
    const updateTracking: Partial<Invoice> = { prm: row.prm, timestamp: row.timestamp, tracking: selectedValue };

    this.invoicesService.putInvoices(updateTracking).subscribe({
      next: () => {
        const index = this.pageInvoices.findIndex((inv) => inv.timestamp === row.timestamp);
        if (index !== -1) {
          this.pageInvoices[index] = { ...this.pageInvoices[index], tracking: selectedValue };
          this.pageable = { ...this.pageable, content: this.pageInvoices };
        }
        this.scheduleSilentRefresh();
      },
      error: (err) => console.error('Erreur lors de la mise à jour', err),
    });
  }

  downloadInvoice(event: Event, invoice: Invoice, index: number) {
    this.downloadLoaders[index] = true;
    const documentId = invoice.documentId!;
    const documentSite = invoice.site.name.replace(/[\/\\:*?"<>|]/g, '_').replace(/\s+/g, '_');
    const downloadName = `${documentId.replace('-B', '')}-${documentSite}.pdf`;

    this.invoicesService.downloadInvoice(documentId).subscribe({
      next: (response: { data: string }) => {
        const a = document.createElement('a');
        a.href = `data:application/pdf;base64,${response.data}`;
        a.download = downloadName;

        a.click();
        this.downloadLoaders[index] = false;
      },
      error: () => {
        this.alertService.error(MESSAGES.DOWNLOAD_ERROR);
        this.downloadLoaders[index] = false;
      },
    });
  }

  /**
   * Méthode récupérant le type de validation choisi dans le toggle
   * @param validationType Le type de validation
   * @param data la facture
   */
  setSelectedValidation(validationType: string, data: Invoice) {
    const invoiceIndex = this.pageInvoices.findIndex((invoice) => invoice.documentId === data.documentId);
    if (this.pageInvoices[invoiceIndex].status === validationType) return;
    if (validationType === Status.VALIDATED && this.isValidPrice(this.pageInvoices[invoiceIndex])) {
      this.showConfirmValidationDialog(invoiceIndex);
      return;
    }
    this.putInvoices(invoiceIndex, validationType as Status);
  }

  /**
   * Méthode permettant de mettre a jour la facture en base de donnée
   * @param i L'index de la facture
   * @param status Le status à mettre en bdd
   */
  private putInvoices(i: number, status: Status) {
    this.invoicesService.putInvoices(this.getObjectInvoice(i, { status })).subscribe({
      next: () => {
        const previousStatus = this.pageInvoices[i].status;
        this.pageInvoices[i].status = status;

        if (previousStatus !== status) {
          if (status === Status.VALIDATED) this.invoicesValidateNumber++;
          else if (previousStatus === Status.VALIDATED) this.invoicesValidateNumber--;
        }
      },
      error: () => this.alertService.error(MESSAGES.STATUS_ERROR),
    });
  }

  /**
   * Méthode permettant de récupérer l'objet pour le put invoice
   * @param i L'index de la facture
   * @param properties paramètre de la facture
   * @private
   */
  private getObjectInvoice(i: number, properties: Partial<Invoice>): Partial<Invoice> {
    return {
      ...properties,
      prm: this.pageInvoices[i]?.prm,
      timestamp: this.pageInvoices[i]?.timestamp,
    };
  }

  /**
   * Méthode pour le rafraichissement des factures dans le tableau
   */
  async refreshInvoices() {
    this.rangeStartDate = '';
    this.rangeEndDate = '';
    this.selectedFilter = '';
    this.currentPage = 0;

    await this.fetchInvoices();
  }

  isSendable() {
    return this.invoicesValidateNumber >= this.invoicesNumberForSend;
  }

  /**
   * Méthode pour l'envoi en masse des mails : porte sur l'intégralité du résultat
   * courant (recherche + filtres actifs), pas uniquement la page affichée.
   */
  async sendAllMail() {
    this.loaderSending = true;

    try {
      const response = await lastValueFrom(this.invoicesService.getInvoices(this.buildRequestParams(false)));
      const invoicesToSend = response.list.Items.filter(
        (invoice) => Status.VALIDATED === invoice.status || Status.GENERATED === invoice.status,
      );
      this.sendMail(invoicesToSend);
    } catch {
      this.alertService.error(MESSAGES.SEND_MAIL_ERROR);
      this.loaderSending = false;
    }
  }

  /**
   * Méthode pour l'envoi d'un mail
   */
  sendOneMail(i: number) {
    this.sendingLoaders[i] = true;
    this.sendMail([this.pageInvoices[i]]);
    this.sendingLoaders[i] = false;
  }

  /**
   * Méthode pour l'envoi en masse des mails
   */
  sendMail(invoicesToSend: Invoice[]) {
    this.invoicesService.sendInvoices(invoicesToSend).subscribe({
      next: () => {
        this.alertService.success('Factures envoyées avec succès');
        this.refreshInvoices().then();
        this.loaderSending = false;
      },
      error: () => {
        this.alertService.error(MESSAGES.SEND_MAIL_ERROR);
        this.loaderSending = false;
      },
    });
  }

  /**
   * Méthode permettant de mettre à jour les factures en fonction de la valeur selectionnée (en cours ou archivé)
   *
   * @param status Le nouveau statut choisi entre en cours ou archivé
   */
  async invoiceStatus(status: string) {
    this.invoicesStatus = status as InvoicesStatusEnum;

    this.rangeStartDate = '';
    this.rangeEndDate = '';
    this.selectedFilter = '';
    this.columnFilters = {};
    this.filterItems = {};
    this.currentPage = 0;
    this.setDefaultSort();

    if (this.invoicesStatus === InvoicesStatusEnum.ERROR) {
      this.searchTerm = '';
      this.appliedSearch = '';
    }

    this.risSelection.clear();
    this.selectedInvoices.clear();

    await this.fetchInvoices();
  }

  /**
   * Méthode permettant l'ouverture de la pop-up avec les bons paramètres
   * @param data la facture
   */
  showCommentDialog(data: Invoice) {
    const options: ConfirmDialogData = {
      textArea: data.comment ? data.comment : '',
      title: 'Ajouter un commentaire',
      confirmText: 'Enregistrer',
      placeholder: 'Veuillez saisir votre commentaire',
      cancelText: 'Annuler',
      enableConfirmButton: true,
      width: '50rem',
      readOnly: false,
      confirmFunction: (value) => {
        const updateInvoice: Partial<Invoice> = {
          prm: data.prm,
          timestamp: data.timestamp,
          comment: value,
        };
        this.invoicesService.putInvoices(updateInvoice).subscribe({
          next: () => {
            this.pageInvoices = this.pageInvoices.map((inv) =>
              inv.timestamp === data.timestamp ? { ...inv, comment: value } : inv,
            );
            this.pageable = { ...this.pageable, content: this.pageInvoices };
          },
          error: () => {},
        });
      },
    };
    this.dialogService.open(options);
  }

  showConfirmValidationDialog(i: number) {
    const data = this.pageInvoices[i!];
    const options: ConfirmDialogData = {
      placeholder: `La facture ${data.documentId} est en dessous du seuil de ${this.threshold} euros. Voulez-vous valider la facture avant de l'envoyer ?`,
      title: 'Confirmer la validation',
      confirmText: 'Oui',
      cancelText: 'Non',
      enableConfirmButton: true,
      width: '50rem',
      readOnly: true,
      confirmFunction: () => {
        this.putInvoices(i, Status.VALIDATED);
      },
    };
    this.dialogService.open(options);
  }

  showConfirmDeleteDialog() {
    const options: ConfirmDialogData = {
      placeholder:
        this.risSelection.selected.length === 1
          ? `Voulez-vous supprimer la facture sélectionnée ?`
          : `Voulez-vous supprimer les ${this.risSelection.selected.length} factures sélectionnées ?`,
      title: 'Confirmer la suppression',
      confirmText: 'Oui',
      cancelText: 'Non',
      enableConfirmButton: true,
      width: '50rem',
      readOnly: true,
      confirmFunction: () => {
        this.deleteInvoices();
      },
    };
    this.dialogService.open(options);
  }

  isStatus(data: unknown, status: Status) {
    return (data as Invoice)?.status === status;
  }

  isValidPrice(data: unknown) {
    const resCt = (data as Invoice)?.RES_CT;
    return resCt?.montant && parseInt(resCt.montant, 10) < this.threshold;
  }

  isCheckable(data: unknown) {
    const status = (data as Invoice)?.status;
    return Status.GENERATED === status || Status.VALIDATED === status || Status.REFUSED === status;
  }

  isSelectable(data: Invoice) {
    return (
      this.invoicesStatus === InvoicesStatusEnum.EN_COURS ||
      (this.invoicesStatus === InvoicesStatusEnum.ARCHIVE && data.typeBill === TypeBill.DRAFT)
    );
  }

  /**
   * Vérifie si le statut est généré, validé ou refusé.
   * @param status Le statut à vérifier.
   * @returns true si le statut est généré, validé ou refusé, sinon false.
   */
  isStatusGeneratedValidatedOrRefused(status: Status): boolean {
    return [Status.GENERATED, Status.VALIDATED, Status.REFUSED].includes(status);
  }

  deleteInvoices() {
    const deleteList = this.risSelection.selected.map((key) => {
      const invoice = this.selectedInvoices.get(key);
      if (invoice) return invoice;

      const [timestamp, prm] = key.split('::');
      return { timestamp, prm };
    });
    this.invoicesService.deleteInvoices(deleteList).subscribe({
      next: (response) => {
        const errorDelete = response.filter((item) => item.status === 'Error');
        if (errorDelete.length > 0) {
          this.alertService.error(
            `Erreur lors de la suppresion des factures : ${errorDelete.map((item) => item.id).join(', ')}`,
          );
        } else {
          this.alertService.success('Factures supprimées avec succès');
        }
        this.refreshInvoices().then();
        this.risSelection.clear();
        this.selectedInvoices.clear();
      },
      error: (err) => {
        console.log(err);
        this.alertService.error(MESSAGES.DELETE_ERROR);
      },
    });
  }

  getInvoiceId(data: Invoice) {
    if (data.typeBill === 'DRAFT' || data.status === 'GENERATING') {
      return '-';
    }
    if (this.invoicesStatus === InvoicesStatusEnum.EN_COURS) {
      return data.documentId?.replace('-B', '');
    }
    return data.documentId;
  }

  getEmails(data: Invoice) {
    return data.contacts?.map((contact) => contact.email).join(', ') || '-';
  }

  getInvoicePeriod(start: string, end: string) {
    const format = 'dd/MM/yyyy';
    const startDate = DateTime.fromISO(start).toFormat(format);
    const endDate = DateTime.fromISO(end).toFormat(format);
    return `${startDate} - ${endDate}`;
  }

  /**
   * Exporte l'intégralité du résultat courant (recherche + filtres actifs),
   * pas uniquement la page affichée.
   */
  async exportInvoices() {
    try {
      const response = await lastValueFrom(this.invoicesService.getInvoices(this.buildRequestParams(false)));
      const invoices = response.list.Items.map((item: Invoice) => normalizeQuantityToMWh(item));

      if (this.invoicesStatus === InvoicesStatusEnum.ARCHIVE) {
        this.exportArchivedInvoices(invoices);
      } else {
        this.exportTodoInvoices(invoices);
      }
    } catch (error) {
      console.error("Erreur lors de l'export des factures", error);
      this.alertService.error(MESSAGES.DEFAULT_ERROR);
    }
  }

  getSignedVolume(quantity: string | undefined, typeBill: string): string {
    if (!quantity) return '-';
    return typeBill === 'AUTOAVOIR' ? String(-quantity) : quantity;
  }

  private exportArchivedInvoices(invoices: Invoice[]) {
    const rows: InvoiceArchivedCsvRow[] = invoices.map((invoice) => ({
      facture: this.getInvoiceId(invoice) || '-',
      producteur: invoice.producer.name,
      site: invoice.site.name,
      prm: invoice.prm,
      filiere: invoice.site.technology,
      periodeFacturation: this.getInvoicePeriod(invoice.DateDebutCalcul, invoice.DateFinCalcul),
      dateEmission: DateTime.fromFormat(invoice.sentDate, 'yyyy-MM-dd HH:mm').toFormat('dd/MM/yyyy HH:mm'),
      montantHt: invoice.RES_CT?.montant || '-',
      volumeMWh: this.getSignedVolume(invoice.quantity, invoice.typeBill),
      formule: formatFormulaLabel(invoice.formulaLabel, invoice.tunnelPercentage),
      typeBill: mapTypeBill(invoice.typeBill),
      typeM0: invoice.ArticleDetails[0]?.typePrix ? mapPriceTypeForExport(invoice.ArticleDetails[0]?.typePrix) : '-',
      suivi: invoice.tracking || '-',
      contact: this.getEmails(invoice),
      commentaire: invoice.comment || '-',
    }));

    downloadCsv(
      `billing_factures_archivees_${DateTime.now().toFormat('yyyyLLdd')}.csv`,
      toCsv(rows, invoiceArchivedHeaders),
    );
  }

  private exportTodoInvoices(invoices: Invoice[]) {
    const rows: InvoiceTodoCsvRow[] = invoices.map((invoice) => ({
      etat: invoice.status,
      dateGeneration: DateTime.fromFormat(invoice.dateGenerated, 'yyyy-MM-dd HH:mm').toFormat('dd/MM/yyyy HH:mm'),
      facture: this.getInvoiceId(invoice) || '-',
      producteur: invoice.producer.name,
      site: invoice.site.name,
      prm: invoice.allPrms,
      filiere: invoice.site.technology,
      periodeFacturation: this.getInvoicePeriod(invoice.DateDebutCalcul, invoice.DateFinCalcul),
      montantHt: invoice.RES_CT?.montant || '-',
      volumeMWh: this.getSignedVolume(invoice.quantity, invoice.typeBill),
      formule: formatFormulaLabel(invoice.formulaLabel, invoice.tunnelPercentage),
      typeBill: mapTypeBill(invoice.typeBill),
      typeM0: invoice.ArticleDetails[0]?.typePrix ? mapPriceTypeForExport(invoice.ArticleDetails[0]?.typePrix) : '-',
      contact: this.getEmails(invoice),
      commentaire: invoice.comment || '-',
    }));

    downloadCsv(
      `billing_factures_en_cours_${DateTime.now().toFormat('yyyyLLdd')}.csv`,
      toCsv(rows, invoiceTodoHeaders),
    );
  }

  updateSingleInvoiceRow(newItem: Invoice | null, oldItem: Invoice | null, type: WebsocketEventType): void {
    // Quantité toujours affichée en MWh, même pour une facture pré-MEP reçue en kWh
    newItem = newItem ? normalizeQuantityToMWh(newItem) : newItem;

    switch (type) {
      case WebsocketEventType.INVOICE_DELETED:
      case WebsocketEventType.INVOICE_SENT:
      case WebsocketEventType.INVOICE_ADDED:
        this.scheduleSilentRefresh();
        break;

      case WebsocketEventType.INVOICE_DELIVERY_UPDATED:
      case WebsocketEventType.INVOICES_UPDATED: {
        if (!newItem) return;
        const index = this.pageInvoices.findIndex(
          (inv) => inv.prm === oldItem?.prm && inv.timestamp === oldItem?.timestamp,
        );
        if (index !== -1) {
          this.pageInvoices = this.pageInvoices.map((inv, i) => (i === index ? { ...inv, ...newItem } : inv));
          this.pageable = { ...this.pageable, content: this.pageInvoices };
        }
        break;
      }
    }
  }

  openSendDialog(i: number) {
    const options: ConfirmDialogData = {
      title: "Confimer l'envoi",
      placeholder: 'Voulez-vous envoyer la facture archivée ?',
      readOnly: true,
      cancelText: 'Non',
      confirmText: 'Oui',
      enableConfirmButton: true,
      width: '50rem',
      confirmFunction: () => {
        this.sendOneMail(i);
      },
    };
    this.dialogService.open(options);
  }

  showTooltip(id: number) {
    this.hoveredTooltipId = id;
  }

  hideTooltip() {
    this.hoveredTooltipId = null;
  }
}
